import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import { cn } from '../cn.js';

type ToastItem = { id: number; title: string; description?: string; tone: 'success' | 'danger' | 'neutral' };
const ToastContext = createContext<(t: Omit<ToastItem, 'id'>) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((t: Omit<ToastItem, 'id'>) => {
    setItems((prev) => [...prev.slice(-2), { ...t, id: Date.now() + Math.random() }]);
  }, []);

  return (
    <ToastContext.Provider value={push}>
      <ToastPrimitive.Provider swipeDirection="right" duration={4500}>
        {children}
        {items.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            onOpenChange={(open) => !open && setItems((prev) => prev.filter((x) => x.id !== t.id))}
            className="flex items-start gap-2.5 rounded-lg border border-line bg-bg px-3.5 py-3 shadow-pop data-[state=open]:animate-pop-in"
          >
            {t.tone === 'success' && <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />}
            {t.tone === 'danger' && <AlertCircle className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden="true" />}
            <div className="min-w-0 flex-1">
              <ToastPrimitive.Title className="text-sm font-medium text-fg">{t.title}</ToastPrimitive.Title>
              {t.description && (
                <ToastPrimitive.Description className={cn('mt-0.5 text-sm text-fg-3')}>{t.description}</ToastPrimitive.Description>
              )}
            </div>
            <ToastPrimitive.Close className="rounded-sm p-0.5 text-fg-4 hover:text-fg" aria-label="Fechar">
              <X className="size-3.5" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed right-4 bottom-4 z-[60] flex w-[360px] max-w-[calc(100vw-32px)] flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
